import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Loader2, ArrowLeft, Search,
  CheckCheck, Check, Briefcase, Plus, X
} from 'lucide-react';

const POLL_MS = 3000; // 3-second message poll

export default function MessagesPage() {
  const { api, user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');

  const messagesEndRef = useRef(null);
  const pollRef       = useRef(null);
  const inputRef      = useRef(null);

  // On mount: if ?with=userId&context=... is in URL, auto-start/open conversation
  useEffect(() => {
    const withUser = searchParams.get('with');
    const contextLabel = searchParams.get('context') || '';
    const contextId    = searchParams.get('context_id') || '';

    fetchConversations().then(convos => {
      if (withUser) {
        const existing = convos?.find(c => c.participants?.includes(withUser));
        if (existing) {
          setActiveConvo(existing);
        } else {
          // Create new conversation
          api.post('/chat/conversations', {
            recipient_id: withUser,
            context_label: contextLabel,
            context_id: contextId,
            context_type: contextId ? 'application' : undefined,
          }).then(res => {
            setActiveConvo(res.data);
            fetchConversations();
          }).catch(() => toast.error('Could not start conversation'));
        }
      }
    });
  }, []);

  useEffect(() => {
    if (activeConvo) {
      fetchMessages(activeConvo.id);
      startPolling(activeConvo.id);
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      stopPolling();
    }
    return stopPolling;
  }, [activeConvo?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await api.get('/chat/conversations');
      setConversations(res.data);
      return res.data;
    } catch { return []; }
    finally { setLoading(false); }
  };

  const fetchMessages = async (convoId) => {
    try {
      const res = await api.get(`/chat/conversations/${convoId}/messages`);
      setMessages(res.data);
    } catch {}
  };

  const startPolling = (convoId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/chat/conversations/${convoId}/messages`);
        setMessages(prev => {
          if (res.data.length !== prev.length) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }
          return res.data;
        });
        // Also refresh convo list for unread counts
        fetchConversations();
      } catch {}
    }, POLL_MS);
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConvo) return;
    const text = newMessage.trim();
    setSending(true);
    // Optimistic add
    const optimistic = {
      id: `opt-${Date.now()}`,
      sender_id: user?.id,
      sender_name: user?.full_name,
      content: text,
      created_at: new Date().toISOString(),
      is_read: false,
      _optimistic: true,
    };
    setMessages(prev => [...prev, optimistic]);
    setNewMessage('');
    try {
      const res = await api.post(`/chat/conversations/${activeConvo.id}/messages`, { content: text });
      setMessages(prev => prev.map(m => m.id === optimistic.id ? res.data : m));
      fetchConversations();
    } catch {
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
      setNewMessage(text);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getOtherName = (convo) => {
    const idx = convo.participants?.indexOf(user?.id);
    return convo.participant_names?.[idx === 0 ? 1 : 0] || 'User';
  };

  const getOtherInitial = (convo) => getOtherName(convo).charAt(0).toUpperCase();

  const filteredConvos = conversations.filter(c =>
    !search || getOtherName(c).toLowerCase().includes(search.toLowerCase())
  );

  const formatTime = (dt) => {
    const d = new Date(dt);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="bg-[#003366] text-white py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <MessageSquare className="w-6 h-6" />Messages
          </h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 rounded-2xl overflow-hidden border shadow-lg bg-white" style={{ height: '75vh' }}>

          {/* ── Conversation List ── */}
          <div className={`border-r flex flex-col ${activeConvo ? 'hidden md:flex' : 'flex'}`}>
            <div className="p-3 border-b bg-gray-50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input className="pl-9 h-9 text-sm" placeholder="Search conversations…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center p-8"><Loader2 className="w-5 h-5 animate-spin text-[#003366]" /></div>
              ) : filteredConvos.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">No conversations yet</p>
                </div>
              ) : filteredConvos.map(convo => {
                const isActive = activeConvo?.id === convo.id;
                const hasUnread = convo.unread_count > 0;
                return (
                  <button
                    key={convo.id}
                    onClick={() => setActiveConvo(convo)}
                    className={`w-full p-4 text-left hover:bg-gray-50 border-b transition-colors ${isActive ? 'bg-[#003366]/5 border-l-4 border-l-[#003366]' : 'border-l-4 border-l-transparent'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative shrink-0">
                        <div className="w-10 h-10 rounded-full bg-[#003366] flex items-center justify-center text-white text-sm font-bold">
                          {getOtherInitial(convo)}
                        </div>
                        {hasUnread && (
                          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-[#E11D48] rounded-full border-2 border-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <p className={`text-sm truncate ${hasUnread ? 'font-bold text-foreground' : 'font-medium'}`}>
                            {getOtherName(convo)}
                          </p>
                          {convo.last_message_at && (
                            <span className="text-xs text-muted-foreground shrink-0">{formatTime(convo.last_message_at)}</span>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-1 mt-0.5">
                          <p className={`text-xs truncate ${hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                            {convo.last_message || 'No messages yet'}
                          </p>
                          {hasUnread && (
                            <span className="shrink-0 bg-[#E11D48] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                              {convo.unread_count > 9 ? '9+' : convo.unread_count}
                            </span>
                          )}
                        </div>
                        {convo.context_label && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-[#003366] bg-[#003366]/10 px-1.5 py-0.5 rounded mt-1">
                            <Briefcase className="w-2.5 h-2.5" />{convo.context_label}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Message Area ── */}
          <div className={`col-span-2 flex flex-col ${!activeConvo ? 'hidden md:flex' : 'flex'}`}>
            {!activeConvo ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-15" />
                  <p className="font-medium">Select a conversation</p>
                  <p className="text-sm mt-1">or start one from the Applicants page</p>
                </div>
              </div>
            ) : (
              <>
                {/* Convo header */}
                <div className="px-4 py-3 border-b flex items-center gap-3 bg-gray-50 shrink-0">
                  <button onClick={() => { setActiveConvo(null); setMessages([]); }} className="md:hidden p-1 -ml-1 text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="w-9 h-9 rounded-full bg-[#003366] flex items-center justify-center text-white text-sm font-bold shrink-0">
                    {getOtherInitial(activeConvo)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{getOtherName(activeConvo)}</p>
                    {activeConvo.context_label && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />{activeConvo.context_label}
                      </p>
                    )}
                  </div>
                  <button
                    className="text-muted-foreground hover:text-foreground p-1 md:hidden"
                    onClick={() => { setActiveConvo(null); setMessages([]); }}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                  {messages.length === 0 && (
                    <div className="text-center text-muted-foreground text-sm py-8">
                      No messages yet. Say hello!
                    </div>
                  )}
                  {messages.map((msg, i) => {
                    const isOwn = msg.sender_id === user?.id;
                    const prevMsg = messages[i - 1];
                    const showName = !isOwn && prevMsg?.sender_id !== msg.sender_id;
                    return (
                      <div key={msg.id} className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
                        {showName && <p className="text-xs text-muted-foreground mb-1 ml-1">{msg.sender_name}</p>}
                        <div className={`max-w-[72%] px-3.5 py-2.5 rounded-2xl text-sm ${
                          isOwn
                            ? 'bg-[#003366] text-white rounded-br-sm'
                            : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                        } ${msg._optimistic ? 'opacity-70' : ''}`}>
                          <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                            <span className={`text-[10px] ${isOwn ? 'text-white/60' : 'text-gray-400'}`}>
                              {formatTime(msg.created_at)}
                            </span>
                            {isOwn && (
                              msg.is_read
                                ? <CheckCheck className="w-3 h-3 text-blue-300" />
                                : <Check className="w-3 h-3 text-white/50" />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="px-4 py-3 border-t flex gap-2 shrink-0 bg-white">
                  <Input
                    ref={inputRef}
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                    placeholder="Type a message… (Enter to send)"
                    className="flex-1 rounded-xl"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={sending || !newMessage.trim()}
                    className="bg-[#003366] hover:bg-[#003366]/90 rounded-xl px-4"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
